/** @format */

import { useCallback, useEffect } from "react";
import toast from "react-hot-toast";
import { AiOutlineShoppingCart } from "react-icons/ai";
import { FaEye, FaHeart, FaTrash } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  add_To_card,
  get_wishlists_products,
  messageClear,
  remove_wishlist_product,
} from "../../store/reducers/cardReducers";
import { devise } from "../../utils/utils";
import Ratings from "../Ratings";

/* ─── Empty State ───────────────────────────────────────────── */
const EmptyWishlist = () => (
  <div className='col-span-full flex flex-col items-center justify-center py-24 px-6 text-center'>
    {/* Animated heart icon */}
    <div className='relative mb-6'>
      <div
        className='w-24 h-24 rounded-full flex items-center justify-center'
        style={{ background: "rgba(239,68,68,0.08)" }}
      >
        <FaHeart size={40} className='text-red-400' style={{ opacity: 0.5 }} />
      </div>
      <span
        className='absolute -top-1 -right-1 w-6 h-6 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center'
        style={{ boxShadow: "0 2px 8px rgba(239,68,68,0.4)" }}
      >
        0
      </span>
    </div>

    <h3 className='text-xl font-bold text-slate-800 mb-2'>
      Votre liste de souhaits est vide
    </h3>
    <p className='text-sm text-slate-400 max-w-xs leading-relaxed mb-6'>
      Ajoutez des produits a votre liste pour les retrouver facilement et ne pas
      les manquer.
    </p>
    <Link
      to='/'
      className='inline-flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-semibold text-white transition-all duration-200 hover:opacity-90 active:scale-95'
      style={{
        background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
        boxShadow: "0 2px 12px rgba(59,130,246,0.35)",
      }}
    >
      <AiOutlineShoppingCart size={15} />
      Decouvrir des produits
    </Link>
  </div>
);

/* ─── Wishlist Item Card ────────────────────────────────────── */
const WishlistCard = ({ item, onRemove, onAddToCart }) => {
  const discountedPrice = item.discount
    ? Math.floor((parseFloat(item.price) * (100 - item.discount)) / 100)
    : item.price;

  return (
    <article
      className='bg-white rounded-2xl overflow-hidden group transition-all duration-300 hover:-translate-y-1'
      style={{
        border: "1px solid rgba(0,0,0,0.07)",
        boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
      }}
      onMouseEnter={(e) =>
        (e.currentTarget.style.boxShadow = "0 8px 32px rgba(0,0,0,0.11)")
      }
      onMouseLeave={(e) =>
        (e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.06)")
      }
    >
      {/* ── Image Zone ── */}
      <div className='relative aspect-square overflow-hidden bg-gradient-to-br from-slate-50 to-gray-100'>
        {/* Discount badge */}
        {item.discount > 0 && (
          <span
            className='absolute top-3 left-3 z-20 inline-flex items-center justify-center px-2.5 py-1 text-xs font-black text-white rounded-full'
            style={{
              background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
              boxShadow: "0 2px 8px rgba(239,68,68,0.4)",
            }}
          >
            -{item.discount}%
          </span>
        )}

        {/* Remove button (top-right) */}
        <button
          onClick={() => onRemove(item._id)}
          title='Retirer de la liste'
          className='absolute top-3 right-3 z-20 w-9 h-9 flex items-center justify-center rounded-full bg-white/80 backdrop-blur-sm text-red-400 transition-all duration-200 hover:bg-red-500 hover:text-white hover:scale-110'
          style={{
            border: "1px solid rgba(239,68,68,0.2)",
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          }}
        >
          <FaTrash size={12} />
        </button>

        {/* Product image */}
        <Link to={`/product/details/${item.productId}`}>
          <img
            className='w-full h-full object-contain p-5 transition-transform duration-500 group-hover:scale-105'
            src={item.image?.url || item.image}
            alt={item.name}
            loading='lazy'
          />
        </Link>

        {/* Hover action bar */}
        <div
          className='absolute bottom-0 left-0 right-0 z-20 flex justify-center gap-2 pb-3 pt-8
                     opacity-0 translate-y-2 transition-all duration-400
                     group-hover:opacity-100 group-hover:translate-y-0
                     md-lg:opacity-100 md-lg:translate-y-0'
          style={{
            background:
              "linear-gradient(to top, rgba(255,255,255,0.95) 0%, transparent 100%)",
          }}
        >
          {/* View */}
          <Link
            to={`/product/details/${item.productId}`}
            title='Voir le produit'
            className='w-9 h-9 flex items-center justify-center rounded-full bg-white text-slate-600 transition-all duration-200 hover:bg-[#c1e63f] hover:text-orange-600 hover:rotate-[360deg]'
            style={{
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              border: "1px solid rgba(0,0,0,0.06)",
            }}
          >
            <FaEye size={13} />
          </Link>

          {/* Add to cart */}
          <button
            onClick={() => onAddToCart(item.productId)}
            title='Ajouter au panier'
            className='w-9 h-9 flex items-center justify-center rounded-full bg-white text-slate-600 transition-all duration-200 hover:bg-[#ad393f] hover:text-white hover:rotate-[360deg]'
            style={{
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              border: "1px solid rgba(0,0,0,0.06)",
            }}
          >
            <AiOutlineShoppingCart size={14} />
          </button>

          {/* Remove */}
          <button
            onClick={() => onRemove(item._id)}
            title='Retirer de la liste'
            className='w-9 h-9 flex items-center justify-center rounded-full bg-white text-slate-600 transition-all duration-200 hover:bg-[#9bcae6] hover:text-red-600 hover:rotate-[360deg]'
            style={{
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              border: "1px solid rgba(0,0,0,0.06)",
            }}
          >
            <FaTrash size={12} />
          </button>
        </div>
      </div>

      {/* ── Card Body ── */}
      <div className='px-4 pt-3 pb-4 flex flex-col gap-2'>
        {/* Product name */}
        <Link
          to={`/product/details/${item.productId}`}
          className='text-sm font-bold text-slate-800 leading-snug line-clamp-2 hover:text-blue-600 transition-colors duration-200'
        >
          {item.name}
        </Link>

        {/* Ratings */}
        {item.rating > 0 && (
          <div className='flex items-center gap-1.5'>
            <Ratings ratings={item.rating} />
          </div>
        )}

        {/* Price */}
        <div
          className='flex items-center justify-between rounded-xl px-3 py-2 mt-1'
          style={{
            background: "linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)",
            border: "1px solid rgba(14,165,233,0.15)",
          }}
        >
          <span className='text-base font-black text-blue-800'>
            {discountedPrice}{" "}
            <span className='text-xs font-normal text-slate-500'>{devise}</span>
          </span>

          {item.discount > 0 && (
            <span className='text-xs text-slate-400 line-through font-medium'>
              {item.price} {devise}
            </span>
          )}
        </div>
      </div>
    </article>
  );
};

/* ─── Main Component ────────────────────────────────────────── */
const WishList = () => {
  const dispatch = useDispatch();
  const { customerInfo } = useSelector((state) => state.auth);
  const { successMessage, messageError, wishlists, deleteError } = useSelector(
    (state) => state.card,
  );

  /* Fetch on mount / id change */
  useEffect(() => {
    if (!customerInfo?.id || customerInfo.id.length !== 24) return;

    const timeout = setTimeout(() => {
      dispatch(get_wishlists_products(customerInfo.id));
    }, 100);

    return () => clearTimeout(timeout);
  }, [customerInfo?.id, dispatch]);

  /* Handle messages */
  useEffect(() => {
    if (deleteError) {
      toast.error(deleteError);
      dispatch(messageClear());
      return;
    }
    if (messageError) {
      dispatch(messageClear());
      return;
    }
    if (successMessage) {
      //toast.success(successMessage);
      dispatch(messageClear());
      if (customerInfo?.id) {
        dispatch(get_wishlists_products(customerInfo.id));
      }
    }
  }, [deleteError, messageError, successMessage, dispatch, customerInfo?.id]);

  /* Handlers */
  const handleRemove = useCallback(
    (id) => dispatch(remove_wishlist_product(id)),
    [dispatch],
  );

  const handleAddToCart = useCallback(
    (productId) => dispatch(add_To_card(productId)),
    [dispatch],
  );

  return (
    <div className='w-full grid grid-cols-4 md-lg:grid-cols-3 md:grid-cols-2 sm:grid-cols-1 gap-6'>
      {wishlists.length === 0 ? (
        <EmptyWishlist />
      ) : (
        wishlists.map((item) => (
          <WishlistCard
            key={item._id}
            item={item}
            onRemove={handleRemove}
            onAddToCart={handleAddToCart}
          />
        ))
      )}
    </div>
  );
};

export default WishList;
